import { Controller } from "@hotwired/stimulus"

// Quick-add campaign notes: PATCH /campaigns/:id/update_notes with JSON, from a popover on the
// index row or the card on the campaign page. The popover is moved to <body> while open and
// positioned under its button, so the table's overflow container cannot clip it (same trick as
// tooltip_controller); it closes on outside click or Escape, and the hover tooltip's template is
// refreshed after a save so the row reflects the new note without a reload.
export default class extends Controller {
  static targets = ["panel", "textarea", "status", "button"]
  static values = { url: String }

  // Targets are scoped to the controller element, so the panel and what it contains are
  // resolved once here — after the panel moves to <body> the target accessors would not find them.
  connect() {
    this.panel = this.hasPanelTarget ? this.panelTarget : null
    this.textarea = this.textareaTarget
    this.status = this.hasStatusTarget ? this.statusTarget : null
    // Stimulus actions stop at the controller element, so the popover's buttons are bound here.
    this.panel?.querySelector("[data-notes-role=save]")?.addEventListener("click", (e) => this.save(e))
    this.panel?.querySelector("[data-notes-role=cancel]")?.addEventListener("click", () => this.close())
    this.onDocumentClick = (e) => { if (this.open && !this.panel.contains(e.target) && !this.element.contains(e.target)) this.close() }
    this.onKey = (e) => { if (e.key === "Escape") this.close() }
    this.onReflow = () => { if (this.open) this.position() }
  }

  disconnect() { this.close() }

  toggle() { this.open ? this.close() : this.show() }

  show() {
    if (!this.panel || this.open) return
    this.placeholder = document.createComment("notes-panel")
    this.panel.replaceWith(this.placeholder)
    document.body.appendChild(this.panel)
    this.panel.hidden = false
    this.panel.style.position = "fixed"
    this.panel.style.zIndex = "60"
    this.open = true
    this.position()
    document.addEventListener("click", this.onDocumentClick, true)
    document.addEventListener("keydown", this.onKey)
    window.addEventListener("scroll", this.onReflow, true)
    window.addEventListener("resize", this.onReflow)
    this.textarea.focus()
  }

  close() {
    if (!this.open) return
    this.open = false
    this.panel.hidden = true
    this.panel.style.position = ""
    this.panel.style.top = this.panel.style.left = ""
    this.placeholder?.replaceWith(this.panel)
    this.placeholder = null
    document.removeEventListener("click", this.onDocumentClick, true)
    document.removeEventListener("keydown", this.onKey)
    window.removeEventListener("scroll", this.onReflow, true)
    window.removeEventListener("resize", this.onReflow)
  }

  // Below the trigger, right-aligned to it, flipped above when there is no room.
  position() {
    const anchor = (this.hasButtonTarget ? this.buttonTarget : this.element).getBoundingClientRect()
    const panel = this.panel.getBoundingClientRect()
    let top = anchor.bottom + 4
    if (top + panel.height > window.innerHeight - 8) top = Math.max(8, anchor.top - panel.height - 4)
    const left = Math.max(8, Math.min(anchor.right - panel.width, window.innerWidth - panel.width - 8))
    this.panel.style.top = `${top}px`
    this.panel.style.left = `${left}px`
  }

  async save(event) {
    event?.preventDefault()
    const token = document.querySelector("meta[name='csrf-token']")?.content
    this.setStatus("Saving…")
    try {
      const res = await fetch(this.urlValue, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ notes: this.textarea.value })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      this.textarea.value = data.notes
      this.reflect(data.notes)
      this.setStatus("Saved")
      setTimeout(() => this.close(), 400)
    } catch (e) {
      this.setStatus(`Not saved (${e.message})`)
    }
  }

  // Keep the row's hover tooltip and button state in step with the saved note.
  reflect(notes) {
    if (this.hasButtonTarget) this.buttonTarget.classList.toggle("text-accent", !!notes)
    const template = this.element.querySelector("template[data-tooltip-target='content']")
    if (!template) return
    template.innerHTML = ""
    const wrap = document.createElement("div")
    if (notes) {
      const title = document.createElement("div"); title.className = "tip-title"; title.textContent = "Note"
      const body = document.createElement("div"); body.className = "whitespace-pre-line break-words"; body.textContent = notes
      wrap.append(title, body)
    } else {
      wrap.className = "tip-muted"; wrap.textContent = "Add a note"
    }
    template.content.appendChild(wrap)
  }

  setStatus(text) { if (this.status) this.status.textContent = text }
}
